/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router';
import {
  IlCard, IlIconInitializer
} from 'il-framework-component';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Wrapper from '../../app/shared/components/Wrapper/Wrapper';
import BackButton from '../../app/shared/components/BackButton/BackButton';
import EnrollAuthenticator from './Methods/Authenticator/EnrollAuthenticator';
import EnrollSMS from './Methods/SMS/EnrollSMS';
import EnrollEmail from './Methods/Email/EnrollEmail';
import styles from './SecuritySetup.scss';
import { I18nNamespaces } from '../../app/shared/utils/constants.util';
import usePreserveSearchNav from '../../app/shared/hooks/usePreserveSearchNav';
import { setError } from './setupSecuritySlice';
import { SecuritySetupRoutes } from '../../app/shared/utils/securitySetupRoutes';

type EnrollMethod = 'authenticator' | 'sms' | 'email';

const VIEWS: Partial<Record<EnrollMethod, React.FC>> = {
  authenticator: EnrollAuthenticator,
  sms: EnrollSMS,
  email: EnrollEmail
};

const Enroll: React.FC = () => {
  const { method } = useParams<{ method: EnrollMethod }>();
  const dispatch = useDispatch();
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const { search } = useLocation();
  const { replacePreserve, goBackOrReplace, replaceQueryParams } = usePreserveSearchNav();

  useEffect(() => {
    if (!VIEWS[method]) replacePreserve(SecuritySetupRoutes.root);
  }, [method, replacePreserve]);

  const MethodView = VIEWS[method];
  if (!MethodView) return null;

  const handleBackClick = (): void => {
    if (method === 'sms') {
      dispatch(setError(''));
      const params = new URLSearchParams(search || '');
      if (params.get('smsFactorId')) {
        replaceQueryParams({ smsFactorId: null });
        return;
      }
    }
    goBackOrReplace(SecuritySetupRoutes.root);
  };

  return (
    <>
      <IlIconInitializer />
      <Wrapper>
        <IlCard className={styles.cardForm}>
          <BackButton
            title={t('loaEnrichment.common.buttons.backToPreviousPage')}
            onClick={handleBackClick}
            className={styles.backBtn}
          />

          {/* This is the only place that switches views */}
          <MethodView />
        </IlCard>
      </Wrapper>
    </>
  );
};

export default Enroll;
